import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { capture } from './lib/posthog';
import { firePageView } from './lib/ga';
import { hasWebappSession } from './lib/lunar-user';
import { useEnsureFullCMS } from './cms';
import Layout from './Layout';
import SupportChatWidget from './SupportChatWidget';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';

/* ─── Lazy-loaded pages (each becomes a separate chunk) ─────────── */
const HomePage = React.lazy(() => import('./pages/HomePage'));
const UseCasesPage = React.lazy(() => import('./pages/UseCasesPage'));
const EnterprisePage = React.lazy(() => import('./pages/EnterprisePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const ClaudeCodePage = React.lazy(() => import('./pages/ClaudeCodePage'));
const ChatGPTPluginPage = React.lazy(() => import('./pages/ChatGPTPluginPage'));
const ApiPage = React.lazy(() => import('./pages/ApiPage'));
const McpPage = React.lazy(() => import('./pages/McpPage'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const CliPage = React.lazy(() => import('./pages/CliPage'));
const CollectionsPage = React.lazy(() => import('./pages/CollectionsPage'));
const FantasyFootballPage = React.lazy(() => import('./pages/FantasyFootballPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const CryptoSocialListeningPage = React.lazy(() => import('./pages/CryptoSocialListeningPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const FAQDetailPage = React.lazy(() => import('./pages/FAQDetailPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const UseCaseDetailPage = React.lazy(() => import('./pages/UseCaseDetailPage'));
const MetricsPage = React.lazy(() => import('./pages/MetricsPage'));
const MetricDetailPage = React.lazy(() => import('./pages/MetricDetailPage'));
const BrandPage = React.lazy(() => import('./pages/BrandPage'));
const ResearchPage = React.lazy(() => import('./pages/ResearchPage'));
const ResearchStudyPage = React.lazy(() => import('./pages/ResearchStudyPage'));

const FAQSlugRedirect: React.FC = () => {
  const { slug } = useParams();
  return <Navigate to={`/support/${slug}`} replace />;
};

/**
 * DEV-12091: gate for routes whose body comes from the lazily-loaded CMS.
 * Suspends (throwing the full-CMS load promise) until the active locale's full
 * data is in, then renders the matched child route. Because it shares the outer
 * <Suspense> boundary with the lazy route chunk, the CMS data and the page code
 * resolve together — the page content is never rendered empty, so no flash.
 */
const RequireFullCMS: React.FC = () => {
  useEnsureFullCMS();
  return <Outlet />;
};

/**
 * Wrapper for "/" that sends signed-in users into the webapp (DEV-10448).
 * Anonymous users see the marketing HomePage as usual.
 *
 * The check is sync-on-first-render (reads localStorage) and uses a
 * full-page nav rather than react-router — "/home" lives in apps/app,
 * not in this Vite bundle. Rendering null while the browser hand-off
 * is queued avoids a flash of the public hero.
 *
 * DEV-10812: gate on `hasWebappSession()` (new-app `lc-auth-token` only),
 * NOT the broad `isSignedIn()`. The legacy `lunar-UserSettings` blob is
 * never cleared on logout and the webapp ignores it, so the old check
 * bounced returning users into an app that immediately showed them
 * logged-out — leaving no way to reach the public homepage.
 *
 * DEV-12528: Preserve locale in redirect to avoid extra server redirect
 * (the webapp's catch-all redirects locale-less paths like `/home` to
 * `/en/home`, adding 200-400ms latency).
 *
 * DEV-12667: stay on `/{locale}/home` (not the locale root). `/es` is the
 * marketing Spanish homepage in local Vite; `/es/home` is the webapp.
 * The catch-all now SSR-renders the ranked table for the `home` slug so
 * this hop settles the same way `/en` does.
 */
const HomeRoute: React.FC = () => {
  const [shouldRedirect] = useState(() =>
    typeof window !== 'undefined' && hasWebappSession()
  );

  useEffect(() => {
    if (shouldRedirect) {
      // Extract locale from current path (/, /es, /de, /fr, /zh)
      const segments = window.location.pathname.split('/').filter(Boolean);
      const locales = ['es', 'de', 'fr', 'zh'];
      const locale = segments.length === 1 && locales.includes(segments[0]) ? segments[0] : 'en';
      window.location.replace(`/${locale}/home`);
    }
  }, [shouldRedirect]);

  if (shouldRedirect) return null;
  return <HomePage />;
};

// Redirect sources must not emit: <Navigate replace> commits the destination
// in the next pathname effect, and that is the one page_view we want.
const MARKETING_REDIRECT_SOURCES = /^\/(faq|faqs|faqs\/.*|legal)$/;

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Track pageview on route change
    capture('$pageview', { $current_url: window.location.href });
    if (!MARKETING_REDIRECT_SOURCES.test(pathname)) {
      firePageView();
    }
  }, [pathname]);

  return null;
};

const AppRouter: React.FC = () => (
  <ChunkLoadErrorBoundary>
    <ScrollToTop />
    <SupportChatWidget />
    <Suspense fallback={null}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomeRoute />} />
          {/* Data-driven pages: metrics glossary, research hub and brand kit
              carry no marketing-CMS body, so they render straight from the
              eager shell without waiting on the full CMS. */}
          <Route path="/metrics" element={<MetricsPage />} />
          <Route path="/metrics/:slug" element={<MetricDetailPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/research/:slug" element={<ResearchStudyPage />} />
          <Route path="/brand" element={<BrandPage />} />
          {/* DEV-12668: pricingPlans + pages.pricing live in the eager CMS seed,
              so /pricing must not wait on the full ~750 KB CMS chunk (blog, FAQs,
              API-docs samples). That parse was the mobile main-thread / paid-LCP
              miss. */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* DEV-12091: marketing pages whose body comes from the lazily-loaded
              CMS. RequireFullCMS suspends until that data is in so the content
              never renders empty. */}
          <Route element={<RequireFullCMS />}>
            <Route path="/use-cases" element={<UseCasesPage />} />
            <Route path="/use-cases/:slug" element={<UseCaseDetailPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/support" element={<FAQPage />} />
            <Route path="/support/:slug" element={<FAQDetailPage />} />
            <Route path="/terms" element={<LegalPage tab="terms" />} />
            <Route path="/privacy" element={<LegalPage tab="privacy" />} />
            <Route path="/referral-terms" element={<LegalPage tab="referral" />} />
            <Route path="/products/lunarcrush-claude" element={<ClaudeCodePage />} />
            <Route path="/products/lunarcrush-chatgpt" element={<ChatGPTPluginPage />} />
            <Route path="/products/lunarcrush-api" element={<ApiPage />} />
            <Route path="/products/lunarcrush-mcpserver" element={<McpPage />} />
            <Route path="/products/lunarcrush-cli" element={<CliPage />} />
            <Route path="/products/lunarcrush-discover" element={<DiscoverPage />} />
            <Route path="/products/lunarcrush-custom-collections" element={<CollectionsPage />} />
            <Route path="/products/fantasy-football" element={<FantasyFootballPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/crypto-social-listening" element={<CryptoSocialListeningPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
          </Route>
          {/* Legacy redirects */}
          {/* DEV-12882: the affiliate program is retired. Both routes 301 to
              /pricing at the edge (functions/_middleware.ts resolveRedirect, with
              a fallback in apps/home/public/_redirects); these client-side
              Navigate redirects keep in-SPA navigation to the old paths from
              hitting a dead route, matching the /faq + /legal pattern. */}
          <Route path="/affiliates" element={<Navigate to="/pricing" replace />} />
          <Route path="/affiliate-resources" element={<Navigate to="/pricing" replace />} />
          <Route path="/faq" element={<Navigate to="/support" replace />} />
          <Route path="/faqs" element={<Navigate to="/support" replace />} />
          <Route path="/faqs/:slug" element={<FAQSlugRedirect />} />
          <Route path="/legal" element={<Navigate to="/privacy" replace />} />
        </Route>
      </Routes>
    </Suspense>
  </ChunkLoadErrorBoundary>
);

export default AppRouter;
